/* ═══════════════════════════════════════════════════════════════════
   Brand Inclusive — theme.js
   IIFE wrapper · idempotent · re-init on Shopify section reload
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ─── Scroll reveal ──────────────────────────────────────────── */
  function initScrollReveal() {
    var els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ─── NavBar scroll state + mobile toggle ────────────────────── */
  function initNavBar(scope) {
    var nav = (scope || document).querySelector('[data-navbar]');
    if (!nav) return;

    var onScroll = function () {
      if (window.scrollY > 60) nav.classList.add('is-scrolled');
      else nav.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var toggle = nav.querySelector('[data-navbar-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        var open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    nav.querySelectorAll('[data-navbar-mobile-link]').forEach(function (link) {
      link.addEventListener('click', function () {
        nav.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* ─── Animated counters ──────────────────────────────────────── */
  function initCounters(scope) {
    var nodes = (scope || document).querySelectorAll('[data-counter]');
    if (!nodes.length) return;

    nodes.forEach(function (node) {
      var target = parseFloat(node.getAttribute('data-counter')) || 0;
      var suffix = node.getAttribute('data-counter-suffix') || '';
      var duration = 2000;
      var started = false;

      var animate = function () {
        if (started) return;
        started = true;
        var startTs = performance.now();
        var step = function (now) {
          var p = Math.min((now - startTs) / duration, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          node.textContent = Math.floor(eased * target) + suffix;
          if (p < 1) requestAnimationFrame(step);
          else node.textContent = target + suffix;
        };
        requestAnimationFrame(step);
      };

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { animate(); io.unobserve(e.target); }
          });
        }, { threshold: 0.5 });
        io.observe(node);
      } else {
        animate();
      }
    });
  }

  /* ─── Hero mousemove (gradient parallax) ─────────────────────── */
  function initHeroParallax(scope) {
    var hero = (scope || document).querySelector('[data-hero]');
    if (!hero) return;
    var bg = hero.querySelector('[data-hero-bg]');
    if (!bg) return;

    if (window.matchMedia('(hover: none)').matches) return;

    hero.addEventListener('mousemove', function (e) {
      var rect = hero.getBoundingClientRect();
      var mx = (e.clientX - rect.left) / rect.width;
      var my = (e.clientY - rect.top) / rect.height;
      bg.style.setProperty('--mx', mx);
      bg.style.setProperty('--my', my);
    });
  }

  /* ─── Testimonials carousel ──────────────────────────────────── */
  function initTestimonials(scope) {
    var root = (scope || document).querySelector('[data-testimonials]');
    if (!root) return;
    var slides = root.querySelectorAll('[data-testimonial]');
    var dots   = root.querySelectorAll('[data-testimonial-dot]');
    if (slides.length < 2) return;

    var idx = 0;
    var interval = parseInt(root.getAttribute('data-autoplay'), 10) || 5000;
    var timer;

    var go = function (next) {
      slides[idx].classList.remove('is-active');
      if (dots[idx]) dots[idx].classList.remove('is-active');
      idx = (next + slides.length) % slides.length;
      slides[idx].classList.add('is-active');
      if (dots[idx]) dots[idx].classList.add('is-active');
    };

    var play = function () {
      stop();
      timer = setInterval(function () { go(idx + 1); }, interval);
    };
    var stop = function () { if (timer) clearInterval(timer); };

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        go(i);
        play();
      });
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', play);

    play();
  }

  /* ─── Boot ───────────────────────────────────────────────────── */
  function boot(scope) {
    initScrollReveal();
    initNavBar(scope);
    initCounters(scope);
    initHeroParallax(scope);
    initTestimonials(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Re-initialise when sections reload in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
  document.addEventListener('shopify:section:select', function (e) {
    boot(e.target);
  });
})();
